// src/api/groupsService.js
import apiClientService from "./ApiClientService";
import {
   flattenGroupAssignment,
   unwrapList,
   unwrapEntity,
} from "../utils/groupFields";

/* ================= helpers ================= */

function safeLang(lang) {
   const v = String(lang || "").toLowerCase();
   return v === "ru" ? "ru" : "ro";
}

function ensurePositiveInt(value, name) {
   const n = Number(value);
   if (!Number.isInteger(n) || n <= 0) {
      throw new Error(`${name} invalid (INT > 0).`);
   }
   return n;
}

function clampInt(value, def, min, max) {
   let n = Number(value);
   if (!Number.isInteger(n)) n = def;
   if (n < min) n = min;
   if (n > max) n = max;
   return n;
}

async function throwIfNotOk(res, fallbackPrefix = "Server error") {
   if (res.ok) return;

   // citim o singură dată body-ul
   const text = await res.text();

   // încercăm să extragem "message" din JSON (dacă backend-ul trimite JSON)
   let msg = text;
   try {
      const j = JSON.parse(text);
      msg =
         j?.message ||
         j?.error ||
         j?.details?.message ||
         (Array.isArray(j?.message) ? j.message.join(", ") : null) ||
         text;
   } catch (_) {
      // text simplu
   }

   throw new Error(`${fallbackPrefix}: ${msg}`);
}

/* ================= existing (admin/groups CRUD) ================= */

export async function getGroups() {
   const response = await apiClientService.get("/groups");
   await throwIfNotOk(response);
   return unwrapList(await response.json()).map(flattenGroupAssignment);
}

export async function getGroupById(id) {
   const gid = ensurePositiveInt(id, "getGroupById: id");
   const response = await apiClientService.get(`/groups/${gid}`);
   await throwIfNotOk(response, "getGroupById failed");
   try {
      return unwrapEntity(await response.json());
   } catch {
      return null;
   }
}

export async function createGroups(payload) {
   const response = await apiClientService.post(
      "/groups",
      JSON.stringify(payload),
   );
   await throwIfNotOk(response);
   try {
      return unwrapEntity(await response.json()) ?? { ...payload };
   } catch {
      return { ...payload };
   }
}

export async function deleteGroup(id) {
   const gid = ensurePositiveInt(id, "deleteGroup: id");
   const response = await apiClientService.delete(`/groups/${gid}`);
   await throwIfNotOk(response);
   return true;
}

export async function patchGroup(id, payload) {
   const gid = ensurePositiveInt(id, "patchGroup: id");
   const response = await apiClientService.patch(
      `/groups/${gid}`,
      JSON.stringify(payload),
   );
   await throwIfNotOk(response);
   try {
      return unwrapEntity(await response.json());
   } catch {
      return true;
   }
}

/* ================= MANAGER <-> GROUP assignment ================= */

/** GET /groups/manager/{managerId} — grupele atribuite unui manager */
export async function getManagerGroups(managerId) {
   const mid = ensurePositiveInt(managerId, "getManagerGroups: managerId");
   const response = await apiClientService.get(`/groups/manager/${mid}`);
   await throwIfNotOk(response, "getManagerGroups failed");
   return unwrapList(await response.json()).map(flattenGroupAssignment);
}

/** POST /groups/manager/{managerId}/assign — atribuie una sau mai multe grupe */
export async function assignGroupsToManager(managerId, groupIds) {
   const mid = ensurePositiveInt(managerId, "assignGroupsToManager: managerId");
   const ids = (Array.isArray(groupIds) ? groupIds : [groupIds])
      .map((g) => Number(g))
      .filter((g) => Number.isInteger(g) && g > 0);
   if (!ids.length) throw new Error("assignGroupsToManager: fără grupe valide.");

   const attempts = [
      { url: `/groups/manager/${mid}/assign`, body: { groupIds: ids } },
      { url: `/groups/manager/${mid}/assign`, body: { groupId: ids[0] } },
      { url: `/groups/manager/${mid}/assign`, body: { groups: ids } },
      { url: `/groups/manager/${mid}/assign`, body: ids },
      {
         url: `/groups/manager/${mid}/assign?groupId=${ids[0]}`,
         body: {},
      },
      {
         url: `/groups/manager/${mid}/assign?groupIds=${ids.join(",")}`,
         body: {},
      },
   ];
   let lastErr = null;

   for (const { url, body } of attempts) {
      const response = await apiClientService.post(url, body);
      if (response.ok || response.status === 409 || response.status === 204) {
         try {
            return await response.json();
         } catch {
            return true;
         }
      }
      try {
         lastErr = await response.text();
      } catch {
         lastErr = `HTTP ${response.status}`;
      }
   }

   throw new Error(
      `Nu s-a putut atribui managerul: ${lastErr || "eroare necunoscută"}`,
   );
}

/** DELETE /groups/manager/{managerId}/group/{groupId} — scoate atribuirea */
export async function removeManagerFromGroup(managerId, groupId) {
   const mid = ensurePositiveInt(managerId, "removeManagerFromGroup: managerId");
   const gid = ensurePositiveInt(groupId, "removeManagerFromGroup: groupId");
   const response = await apiClientService.delete(
      `/groups/manager/${mid}/group/${gid}`,
   );
   await throwIfNotOk(response, "removeManagerFromGroup failed");
   return true;
}

/* ================= PROFESSOR endpoints ================= */

export async function getMyGroupStudents() {
   // ✅ Swagger: GET /api/groups/my-groups/students
   const res = await apiClientService.get("/groups/my-groups/students");
   await throwIfNotOk(res, "getMyGroupStudents failed");
   return await res.json();
}

export async function getMyGroupOverview() {
   // ✅ GET /api/groups/my-group/overview -> { totalGroups, groups:[...] }
   const res = await apiClientService.get("/groups/my-group/overview");
   await throwIfNotOk(res, "getMyGroupOverview failed");
   return await res.json();
}

export async function getStudentPracticeProgress({
   studentId,
   page = 1,
   limit = 20,
} = {}) {
   const sid = ensurePositiveInt(
      studentId,
      "getStudentPracticeProgress: studentId",
   );

   const p = clampInt(page, 1, 1, 999999);
   const l = clampInt(limit, 20, 1, 200);

   const qs = new URLSearchParams();
   qs.set("page", String(p));
   qs.set("limit", String(l));

   const url = `/groups/my-group/students/${sid}/practice-progress?${qs.toString()}`;

   const res = await apiClientService.get(url);
   if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(
         `getStudentPracticeProgress failed (${res.status}): ${text}`,
      );
   }
   const data = await res.json();
   const practiceHistory = Array.isArray(data?.practiceHistory)
      ? data.practiceHistory
      : [];
   return data;
}

export async function getStudentDetailedPracticeSession({
   studentId,
   practiceId,
   lang = "ro",
} = {}) {
   const sid = ensurePositiveInt(
      studentId,
      "getStudentDetailedPracticeSession: studentId",
   );
   const pid = ensurePositiveInt(
      practiceId,
      "getStudentDetailedPracticeSession: practiceId",
   );

   const qs = new URLSearchParams();
   qs.set("practiceId", String(pid));
   qs.set("lang", safeLang(lang));

   const url = `/exams/practice/student/${sid}/detailed?${qs.toString()}`;

   const res = await apiClientService.get(url);
   await throwIfNotOk(res, "getStudentDetailedPracticeSession failed");
   return await res.json();
}
